import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api/client';
import type { TenantInfo } from '../types/auth.types';
import { useAuthStore } from '../store/authStore';

async function fetchTenantInfo(): Promise<TenantInfo> {
  const response = await apiClient.get<{
    tenantId: string;
    identifier: string;
    name: string;
    logo: string;
    primaryColor: string;
    secondaryColor: string;
    azureAdEnabled?: boolean;
  }>('/auth/tenant-info');

  return {
    id: response.data.tenantId,
    identifier: response.data.identifier,
    name: response.data.name,
    logo: response.data.logo,
    primaryColor: response.data.primaryColor,
    secondaryColor: response.data.secondaryColor,
    azureAdEnabled: response.data.azureAdEnabled ?? false,
  };
}

export function useTenantInfo() {
  const setTenantInfo = useAuthStore((s) => s.setTenantInfo);
  const storedTenantInfo = useAuthStore((s) => s.tenantInfo);

  const query = useQuery({
    queryKey: ['tenantInfo'],
    queryFn: fetchTenantInfo,
    staleTime: 5 * 60 * 1000, // 5 min — igual que el TTL de Redis
    retry: false,
    initialData: storedTenantInfo ?? undefined,
    // El valor de localStorage se muestra instantáneo (sin flash), pero se
    // marca como "infinitamente viejo" para que React Query SIEMPRE dispare
    // un refetch en background al montar, sin importar staleTime. Sin esto,
    // un cambio de configuración (ej. habilitar Azure AD) podía quedar
    // invisible para siempre en un navegador que ya tenía el valor viejo
    // guardado — nunca se revalidaba solo con recargar la página.
    initialDataUpdatedAt: storedTenantInfo ? 0 : undefined,
  });

  // Persistir en store cuando lleguen datos frescos
  useEffect(() => {
    if (query.data && query.data !== storedTenantInfo) {
      setTenantInfo(query.data);
    }
  }, [query.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // react-query NO loguea nada en consola cuando una query falla (solo marca
  // isError=true) — si el error ocurre antes de que axios llegue al
  // interceptor de respuesta (ej. armando la URL, o en el interceptor de
  // request), queda completamente invisible: sin red, sin consola, sin
  // App Insights. Este console.error es la única garantía de verlo, sea
  // cual sea el origen — y se reenvía a App Insights como Trace vía el
  // interceptor de consola ya existente (appInsights.service.ts).
  useEffect(() => {
    if (query.isError && query.error) {
      const err = query.error as Error & { code?: string };
      console.error('[useTenantInfo] Fallo al resolver tenant-info', {
        message: err?.message || String(query.error),
        name: err?.name,
        code: err?.code,
        stack: err?.stack,
      });
    }
  }, [query.isError, query.error]);

  // Cualquier error al resolver el tenant (503 TENANT_RESOLUTION_FAILED, 404 tenant no configurado, u otros) muestra la pantalla de servicio no disponible
  const isServiceUnavailable = query.isError;

  return {
    tenantInfo: query.data ?? null,
    isLoading: query.isLoading,
    isServiceUnavailable,
  };
}
